import { doc, getDoc, type DocumentData } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import type {
  AiAnalysisSettings,
  AiCategoryMaster,
  AiCategoryMasterRow,
} from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

/**
 * AI 解析結果の chip 表示用 M2 master 解決 hook (admin 版、user app の
 * `apps/user/src/features/posts/useAiCategoryMasters.ts` の踏襲)。
 *
 * apps/* 間 deep import は禁止 (ESLint boundaries) のため、posts-moderation 専用に
 * minimum scope で複製 (settingsId → M1 → categoryMasterIds[] → M2 fetch)。
 *
 * 戻り値の構造は user app 版と同一 (byCategoryCd / byCategoryLabel / byTemplateVar)。
 */

export interface AiCategoryMastersResult {
  byCategoryCd: Map<string, AiCategoryMasterRow>;
  byCategoryLabel: Map<string, AiCategoryMasterRow>;
  byTemplateVar: Map<string, AiCategoryMaster>;
  masters: AiCategoryMaster[];
  loading: boolean;
}

const _settingsCache = new Map<string, AiAnalysisSettings | null>();
const _masterCache = new Map<string, AiCategoryMaster | null>();

async function loadSettings(settingsId: string): Promise<AiAnalysisSettings | null> {
  if (_settingsCache.has(settingsId)) return _settingsCache.get(settingsId) ?? null;
  try {
    const snap = await getDoc(doc(db, 'aiAnalysisSettings', settingsId));
    if (!snap.exists()) {
      _settingsCache.set(settingsId, null);
      return null;
    }
    const data = { ...(snap.data() as DocumentData), id: settingsId } as AiAnalysisSettings;
    _settingsCache.set(settingsId, data);
    return data;
  } catch {
    _settingsCache.set(settingsId, null);
    return null;
  }
}

async function loadMaster(masterId: string): Promise<AiCategoryMaster | null> {
  if (_masterCache.has(masterId)) return _masterCache.get(masterId) ?? null;
  try {
    const snap = await getDoc(doc(db, 'aiCategoryMasters', masterId));
    if (!snap.exists()) {
      _masterCache.set(masterId, null);
      return null;
    }
    const data = { ...(snap.data() as DocumentData), id: masterId } as AiCategoryMaster;
    _masterCache.set(masterId, data);
    return data;
  } catch {
    _masterCache.set(masterId, null);
    return null;
  }
}

const EMPTY_MAP_RESULT: AiCategoryMastersResult = {
  byCategoryCd: new Map(),
  byCategoryLabel: new Map(),
  byTemplateVar: new Map(),
  masters: [],
  loading: false,
};

export function useAiCategoryMastersBySettings(
  settingsId: string | null | undefined,
): AiCategoryMastersResult {
  const [masters, setMasters] = useState<AiCategoryMaster[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!settingsId) {
      setMasters([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const settings = await loadSettings(settingsId);
      if (cancelled) return;
      const ids = (
        settings?.categoryMasterIds?.length
          ? settings.categoryMasterIds
          : settings?.categoryMasterId
            ? [settings.categoryMasterId]
            : []
      ) as string[];
      const loaded = (await Promise.all(ids.map(loadMaster))).filter(
        (m): m is AiCategoryMaster => !!m,
      );
      if (!cancelled) {
        setMasters(loaded);
        setLoading(false);
      }
    })().catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [settingsId]);

  return useMemo(() => {
    if (!settingsId) return EMPTY_MAP_RESULT;
    const byCategoryCd = new Map<string, AiCategoryMasterRow>();
    const byCategoryLabel = new Map<string, AiCategoryMasterRow>();
    const byTemplateVar = new Map<string, AiCategoryMaster>();
    for (const m of masters) {
      if (m.templateVar) byTemplateVar.set(m.templateVar, m);
      for (const r of m.rows ?? []) {
        if (r.categoryCd && !byCategoryCd.has(r.categoryCd)) byCategoryCd.set(r.categoryCd, r);
        if (r.category && !byCategoryLabel.has(r.category)) byCategoryLabel.set(r.category, r);
      }
    }
    return { byCategoryCd, byCategoryLabel, byTemplateVar, masters, loading };
  }, [settingsId, masters, loading]);
}

export function resolveChipColor(
  row: AiCategoryMasterRow,
  master: AiCategoryMaster | undefined,
): string {
  const fallback = '#94A3B8';
  if (!master) return row.color ?? fallback;
  const axis = master.colorAxis;
  if (axis === 'category' || axis === 'categoryCd') {
    return row.color ?? fallback;
  }
  const key = axis === 'major' ? row.major : row.majorCd;
  if (key && master.colorMap?.[key]) return master.colorMap[key];
  return row.color ?? fallback;
}

/**
 * 行単位の絵文字を解決する (docs/25 §3-1、role_type 等)。色と異なり軸集約は行わず、
 * マスタ行の `icon` をそのまま返す (未設定なら undefined、UI 側は表示を省略する)。
 */
export function resolveChipEmoji(row: AiCategoryMasterRow): string | undefined {
  return row.icon;
}
